import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { MarinaOrder, OrderItem, Sling, SlingConfig } from 'src/app/models';
import { BoatWash } from 'src/app/models/boatWash';
import { BoatStatus, SlingStatus } from 'src/app/models/enums';
import { OptionWashAndTurnEngine } from 'src/app/models/enums/option-wash-and-turn-engine';
import { BoatWashService } from 'src/app/services/boat-wash.service';
import { EngineTurnService } from 'src/app/services/engine-turn.service';
import { MarinaOrderService } from 'src/app/services/marina-order.service';
import { OrderItemService } from 'src/app/services/order-item.service';
import { SlingService } from 'src/app/services/sling.service';
import { ToastService } from 'src/app/services/toast.service';
import { MessageUtil } from 'src/app/utils/message.util';
import { OrderUtil } from 'src/app/utils/order.util';
import { SlingUtil } from 'src/app/utils/sling.util';
import moment from 'moment';
import { EngineTurn } from 'src/app/models/engine-turn';
import Swal from 'sweetalert2';
import { SlingOperationComponent } from '../sling-operation/sling-operation.component';
import { ProductCategoryType } from 'src/app/models/product-category-type';
import { OrderStatus } from 'src/app/models/order-status';
import { SlingOrderDetailsComponent } from '../sling-order-details/sling-order-details.component';
import { SlingFuelCheckComponent } from '../sling-fuel-check/sling-fuel-check.component';
import { SlingConsumableCheckComponent } from '../sling-consumable-check/sling-consumable-check.component';
import { SlingAccessoryCheckComponent } from '../sling-accessory-check/sling-accessory-check.component';
import { DialogService } from 'primeng/dynamicdialog';
import { BroadcastService } from 'src/app/services/broadcast.service';
import { StorageUtil } from 'src/app/utils/storage.util';
import { Subscription } from 'rxjs';
import { WebSocketService } from 'src/app/services/web-socket.service';

@Component({
  selector: 'app-sling-up-tab',
  templateUrl: './sling-up-tab.component.html',
  styleUrls: ['./sling-up-tab.component.scss']
})
export class SlingUpTabComponent implements OnInit {

  @Input()
  isMobile = false;

  @Input()
  slingConfig: SlingConfig;

  @Input()
  slingQueue: number;

  slingsUpCheck: Sling[] = new Array();
  orders: any[];

  referenceTime: any;
  boatStatus: BoatStatus;

  slingSubscription: Subscription;

  constructor(
    private slingService: SlingService,
    private messageUtil: MessageUtil,
    public dialog: DialogService,
    private boatWashService: BoatWashService,
    private engineTurnService: EngineTurnService,
    private router: Router,
    private marinaOrderService: MarinaOrderService,
    private spinner: NgxSpinnerService,
    private orderItemService: OrderItemService,
    public orderUtil: OrderUtil,
    private toasty: ToastService,
    public slingUtil: SlingUtil,
    private webSocketService: WebSocketService
  ) {
    this.referenceTime = new Date();
    this.boatStatus = BoatStatus.STORED;
  }

  async ngOnInit(): Promise<void> {
    this.spinner.show();
    await this.fillSlingTable();
    this.spinner.hide();
    this.slingSubscription = this.webSocketService.getMessageSling().subscribe(async (message) => {
      this.spinner.show();
      await this.fillSlingTable();
      this.spinner.hide();
    });
  }

  ngOnDestroy(): void {
    this.webSocketService.unsubscribeSling(this.slingSubscription);
  }

  async fillSlingTable(): Promise<void> {
    this.slingsUpCheck = new Array();
    const filter = {
      slingStatus: ['PENDING_UP'], 
      boatStatus: ['SAILING', 'MOORED'],
      queueControl: this.slingConfig ? this.slingConfig.queueControl : false, 
      orderBy: 'orderReturn'
    };

    return new Promise<void>(
      async (resolve) => {
        this.slingService.findSlingByFilterV2(filter).subscribe(
          async (data) => {
            this.slingsUpCheck = data;
            this.slingsUpCheck.sort((a, b) => {
              if (!a.slingUpAt && !b.slingUpAt) return 0;
              if (!a.slingUpAt) return -1;
              if (!b.slingUpAt) return 1;
              if (a.slingUpAt > b.slingUpAt) {
                return 1;
              }
              if (a.slingUpAt < b.slingUpAt) {
                return -1;
              }
              return 0;
            });
            
            if (this.slingQueue !== null && this.slingQueue !== undefined && this.slingQueue > 0) {
              this.slingsUpCheck = this.slingsUpCheck.filter(x => x.slingConfigMultQueue && x.slingConfigMultQueue.id === this.slingQueue);
            } 
            const list = this.slingsUpCheck.map((x) => x.boat.id);
            if (list.length > 0) {
              await this.findSlingsNextDay(list);
            }
            resolve();
          }
        );
      }
    );
  }

  updateSling(sling: Sling): void {

    this.slingService.updateOnly(sling).subscribe(
      () => {
        this.fillSlingTable();
        if (sling.boatStatus === BoatStatus.STORED || sling.finishedOnWater) {
          this.verifyWashAndTurnEngine(sling);
        }
        this.toasty.success(this.messageUtil.translateKey('MSG.SLING-SUCCESS'));
      },
      (error) => {
        const exception = error.error.data.exception;
        this.messageUtil.generateMessage(exception.type, exception.title, exception.message);
      }
    );
  }

  private verifyWashAndTurnEngine(sling): void {
    if (this.slingConfig.autoScheduleUpdateWash) {
      if (this.slingConfig.washRunOption === OptionWashAndTurnEngine.AutoRegistration) {
        this.saveWash(sling, false);
      } else {
        this.saveWash(sling, true);
      }
    }

    if (this.slingConfig.autoScheduleUpdateEngineTurn) {
      if (this.slingConfig.engineTurnRunOption === OptionWashAndTurnEngine.AutoRegistration) {
        this.saveTurnEngine(sling, false);
      } else {
        this.saveTurnEngine(sling, true);
      }
    }
  }

  private saveWash(sling: Sling, action: any): void {
    this.createBoatWashFastRegistration(sling, action);
  }

  private saveTurnEngine(sling: Sling, action: any): void {
    this.createEngineTurnFastRegistration(sling, action);
  }

  save(sling): void {
    // EAS-245: ao encerrar (retorno) o PRINCIPAL de um grupo (marina com agendamento múltiplo),
    // pergunta se quer encerrar também as filhas que estão no MESMO status.
    if (this.slingConfig.allowAdditionalBoats && sling.groupPrincipal && sling.slingGroupId) {
      this.slingService.findGroupSiblings(sling.slingGroupId).subscribe(
        (siblings) => {
          const pendentes = (siblings || []).filter(
            (s) => s.id !== sling.id && s.slingStatus === sling.slingStatus);
          if (pendentes.length > 0) {
            const nomes = pendentes.map((s) => s.boatName).join(', ');
            Swal.fire({
              title: 'Encerrar embarcações do grupo?',
              text: `Encerrar também junto: ${nomes}?`,
              icon: 'question',
              showCancelButton: true,
              showDenyButton: true,
              confirmButtonText: 'Encerrar todas',
              denyButtonText: 'Só esta',
              cancelButtonText: 'Cancelar',
              reverseButtons: true,
            }).then((result) => {
              if (result.isConfirmed) {
                this.returnGroup(sling, pendentes);
              } else if (result.isDenied) {
                this.doSave(sling);
              }
            });
          } else {
            this.doSave(sling);
          }
        },
        () => this.doSave(sling)
      );
    } else {
      this.doSave(sling);
    }
  }

  private doSave(sling): void {
    sling.slingUpAt = moment().format('YYYY-MM-DDTHH:mm:ss');
    sling.slingStatus = SlingStatus.OK;
    sling.boatStatus = this.boatStatus;
    this.updateSling(sling);
  }

  // EAS-245: encerra o principal + as irmãs no mesmo status, em lote (backend returnGroup).
  // Após encerrar, dispara o auto-agendamento de lavação/giro de motor para cada embarcação encerrada
  // (mesmo comportamento do retorno individual).
  private returnGroup(sling, pendentes: any[]): void {
    this.slingService.returnGroup(sling.slingGroupId, new Date(), false).subscribe(
      () => {
        this.toasty.success(this.messageUtil.translateKey('MSG.SLING-SUCCESS'));
        // principal + filhas encerradas
        this.verifyWashAndTurnEngine({ boat: { id: sling.boat.id } });
        (pendentes || []).forEach((p) => this.verifyWashAndTurnEngine({ boat: { id: p.boatId } }));
        this.fillSlingTable();
      },
      (error) => {
        const exception = error.error.data.exception;
        this.messageUtil.generateMessage(exception.type, exception.title, exception.message);
      }
    );
  }

  createBoatWashFastRegistration(sling, isActive): void {
    const boatWash = new BoatWash();
    boatWash.boatId = sling.boat.id;
    boatWash.isActive = isActive;
    boatWash.isSea = true;
    this.boatWashService.createFastRegistrarion(boatWash).subscribe(
      () => null,
      (error) => {
        const exception = error.error.data.exception;
        this.messageUtil.generateMessage(exception.type, exception.title, exception.message);
      }
    );
  }

  createEngineTurnFastRegistration(sling, isActive): void {
    const engineTurn = new EngineTurn();
    engineTurn.boatId = sling.boat.id;
    engineTurn.isActive = isActive;
    engineTurn.isSea = true;
    this.engineTurnService.createFastRegistrarion(engineTurn).subscribe(
      () => null,
      (error) => {
        const exception = error.error.data.exception;
        this.messageUtil.generateMessage(exception.type, exception.title, exception.message);
      }
    );
  }

  notEqualDate(slingCheck: Sling): boolean {
    if (new Date(slingCheck.navigationPlan.returnTime).setHours(0, 0, 0, 0) !==
      (new Date(this.referenceTime)).setHours(0, 0, 0, 0)) {
      return true;
    }
    return false;
  }

  openSingleRequest(slingCheck): void {
    if (this.orders.length > 0) {
      this.dialog.open(SlingOrderDetailsComponent, {
        header: 'Embarcação: ' + slingCheck.boat.name + ' - ' + moment().format('DD/MM/YY'),
        data: {
          sling: slingCheck,
          customers: slingCheck.boat.boatCustomers.map((bc) => bc.customer),
          orders: this.orders
        },
        width: '70%'
      });
    } else {
      this.router.navigate(
        ['/single-order'],
        {
          queryParams: {
            customerId: slingCheck.navigationPlan.responsible.id,
            boatId: slingCheck.boat.id,
            slingId: slingCheck.id
          }
        }
      );
    }
  }

  loadMarinaOrders(slingCheck): void {
    this.spinner.show();
    this.marinaOrderService.getDebitsCustomersToDate(new Date().getTime(),
      slingCheck.boat.boatCustomers
        .map((bc) => bc.customer)
        .map((c) => c.id).join()).subscribe(
          (data) => {
            this.orders = data;
            this.spinner.hide();
            this.openSingleRequest(slingCheck);
          },
          (error) => {
            this.spinner.hide();
            const exception = error.error.data.exception;
            this.messageUtil.generateMessage(exception.type, exception.title, exception.message);
          }
        );
  }

  async findSlingsNextDay(boatIds: number[]): Promise<void> {
    return new Promise<void>(
      async (resolve) => {
        this.slingService.findBoatIdsWithSlingsNextDay(boatIds).subscribe(async (ids) => {
          if (ids) {
            // tslint:disable-next-line: prefer-for-of
            for (let index = 0; index < ids.length; index++) {
              const id = ids[index];
              const sling = this.slingsUpCheck.filter((x) => x.boat.id === id)[0];
              if (sling) {
                sling.slingNextDay = true;
              }
            }
          }
          resolve();
        });
      }
    );
  }

  changeBoatStatus(sling: Sling): void {
    if (!sling.finishedOnWater) {
      Swal.fire({
        title: 'Confirmar operação?',
        text: 'O status do barco será alterado para '
          + (sling.boatStatus === BoatStatus.MOORED ?
          this.messageUtil.translateKey('SAILING') :
          this.messageUtil.translateKey('MOORED')),
        showCancelButton: true,
        confirmButtonText: this.messageUtil.translateKey('CONFIRM'),
        cancelButtonText: this.messageUtil.translateKey('CANCEL'),
        showLoaderOnConfirm: true,
        reverseButtons: true,
        cancelButtonColor: '#d33',
        allowOutsideClick: false,
        preConfirm: () => {
          if (sling.boatStatus === BoatStatus.MOORED) {
            sling.boatStatus = BoatStatus.SAILING;
          } else if (sling.boatStatus === BoatStatus.SAILING) {
            sling.boatStatus = BoatStatus.MOORED;
          }
          this.updateSling(sling);
        }
      });
    } else {
      this.messageUtil.generateMessage('warning', 'Lingada finalizada', 'A lingada já se encontra finalizada.');
    }
  }

  handleAction(sling: Sling): void {
    this.dialog.open(SlingOperationComponent, {
      header: 'Confirmação - ' + sling.boat.name + ' - ' + sling.navigationPlan.responsible.name + '',
      data: {
        sling,
        origin: 'SlingUpQueue',
        manageDeck: this.slingConfig.manageDeck,
        isFinishOnWater: this.slingConfig.finishOnWater
      }
    }).onClose.subscribe((result: any) => {
      if (result !== '') {
        if (result.boatStatus === BoatStatus.STORED) {
          this.save(sling);
        } else {
          if (result.finishedOnWater) {
            sling.finishedOnWater = true;
            sling.boatStatus = BoatStatus.MOORED;
          }
          sling.slingStatus = result.slingStatus;
          this.updateSling(sling);
        }
      }
    });
  }

  openDialogFuelOrder(sling: Sling): void {
    const fuel = sling.marinaOrder.orderItems.filter(
      (x) => x.product.productCategory.productCategoryType === ProductCategoryType.Fuel
    )[0];
    this.dialog.open(SlingFuelCheckComponent, {
      data: {
        slingId: sling.id,
        fuelOrder: fuel,
        referenceTime: this.referenceTime,
        fullTankCheck: false
      }
    }).onClose.subscribe((result) => {
      if (result !== null && result !== undefined) {
        Swal.fire({
          title: 'Confirmar operação?',
          showCancelButton: true,
          confirmButtonText: this.messageUtil.translateKey('CONFIRM'),
          cancelButtonText: this.messageUtil.translateKey('CANCEL'),
          showLoaderOnConfirm: true,
          reverseButtons: true,
          cancelButtonColor: '#d33',
          allowOutsideClick: false,
          preConfirm: () => {
            fuel.itemStatus = true;
            fuel.qtySupplied = result;
            fuel.itemValue = fuel.product.price.value;
            fuel.dateSupply = moment().format('YYYY-MM-DDTHH:mm:ss');
            fuel.marinaOrder = sling.marinaOrder;
            fuel.marinaOrder.sling = null;
            fuel.marinaOrder.orderItems = [];
            this.updateOrderItem(new Array(fuel), ProductCategoryType.Fuel);
          }
        });
      }
    });
  }

  updateOrderItem(orderItems: OrderItem[], category: ProductCategoryType): void {
    this.orderItemService.saveChecking(orderItems).subscribe({
      next: () => {
      switch (category) {
        case ProductCategoryType.Consumable:
          this.toasty.success('Produto fornecido com sucesso');
          break;
        case ProductCategoryType.Fuel:
          this.toasty.success('Combustível fornecido com sucesso');
          break;
        default:
          this.toasty.success('Acessório fornecido com sucesso');
          break;
      }
      this.fillSlingTable();
    },
      error: (error) => {
        const exception = error.error.data.exception;
        this.messageUtil.generateMessage(exception.type, exception.title, exception.message);
      }
    });
  }

  openDialogConsumableOrder(sling: Sling): void {
    this.dialog.open(SlingConsumableCheckComponent, {
      data: {
        slingId: sling.id,
        referenceTime: this.referenceTime
      },
      header: this.messageUtil.translateKey('CONSUMABLES')
    }).onClose.subscribe((result) => {
      if (result) {
        const consumables: OrderItem[] = new Array();
        this.validItems(result, result.marinaOrder.orderItems, consumables);
      } else {
        this.fillSlingTable();
      }
    });
  }

  validItems(slingOrder, orderItems: OrderItem[], consumables: OrderItem[]): boolean {
    let typeErrorQty = '';
    for (const consumable of orderItems) {
      if (consumable.qtySupplied == null) {
        this.messageUtil.generateMessage('error', 'SUMMARY.ERROR', 'Informar a quantidade');
        return false;
      }
      if (consumable.qtySupplied > consumable.qtyRequested) {
        typeErrorQty = 'more';
      } else if (consumable.qtySupplied < consumable.qtyRequested) {
        typeErrorQty = 'less';
      }
      consumable.itemStatus = true;
      if (!consumable.product.price.priceFree) {
        consumable.itemValue = consumable.product.price.value;
      }
      consumable.marinaOrder = new MarinaOrder();
      consumable.marinaOrder.id = slingOrder.marinaOrder.id;
      consumables.push(consumable);
    }
    if (typeErrorQty === '') {
      this.saveConsumables(consumables);
    } else {
      this.confirmDifferentConsumable(typeErrorQty, consumables);
    }
  }

  confirmDifferentConsumable(typeErrorQty, consumables): void {
    const titleMsg = 'Quantidade diferente';
    let textMsg = '';
    if (typeErrorQty === 'more') {
      textMsg = 'A quantidade fornecida é superior a solicitada. Deseja continuar?';
    } else {
      textMsg = 'A quantidade fornecida é inferior a solicitada. Deseja continuar?';
    }
    Swal.fire({
      title: titleMsg,
      text: textMsg,
      icon: 'warning',
      reverseButtons: true,
      showCancelButton: true,
      cancelButtonColor: '#d33',
      confirmButtonText: this.messageUtil.translateKey('CONFIRM'),
      cancelButtonText: this.messageUtil.translateKey('CANCEL')
    }).then((response) => {
      if (response.value) {
        this.saveConsumables(consumables);
      }
    });
  }

  saveConsumables(consumables: OrderItem[]): void {
    Swal.fire({
      title: 'Confirmar Lingada?',
      showCancelButton: true,
      confirmButtonText: this.messageUtil.translateKey('CONFIRM'),
      cancelButtonText: this.messageUtil.translateKey('CANCEL'),
      showLoaderOnConfirm: true,
      reverseButtons: true,
      cancelButtonColor: '#d33',
      allowOutsideClick: false,
      preConfirm: () => {
        consumables.forEach((c: OrderItem) => {
          c.dateSupply = moment().format('YYYY-MM-DDTHH:mm:ss');
        });
        this.updateOrderItem(consumables, ProductCategoryType.Consumable);
      }
    });
  }

  openDialogSlingAccessories(sling: Sling): void {
    this.dialog.open(SlingAccessoryCheckComponent, {
      data: {
        slingId: sling.id,
        referenceTime: this.referenceTime
      },
      header: this.messageUtil.translateKey('ACCESSORIES')
    }).onClose.subscribe(() => {
      this.fillSlingTable();
    });
  }

  hasFuel(sling: Sling): boolean {
    const fuels = sling.marinaOrder.orderItems.filter(
      (orderItem) => orderItem.product.productCategory.productCategoryType === ProductCategoryType.Fuel
    );
    return sling.marinaOrder.orderStatus !== OrderStatus.Canceled && fuels.length > 0;
  }

  hasConsumables(sling: Sling): boolean {
    const consumables = sling.marinaOrder.orderItems.filter(
      (orderItem) => orderItem.product.productCategory.productCategoryType === ProductCategoryType.Consumable
    );
    return sling.marinaOrder.orderStatus !== OrderStatus.Canceled && consumables.length > 0;
  }

  hasAcessories(sling: Sling): boolean {
    return sling.marinaOrder.orderStatus !== OrderStatus.Canceled && sling.slingAccessories.length > 0;
  }

  getFuel(orderItems: OrderItem[]): OrderItem {
    const fuels = orderItems.filter((orderItem) => orderItem.product.productCategory.productCategoryType === ProductCategoryType.Fuel);
    if (fuels.length > 0) {
      return fuels[0];
    }
    return new OrderItem();
  }

  isNumber(value: any): boolean {
    return typeof value === 'number';
  }

  countConsumables(orderItems: OrderItem[]): number {
    const consumables = orderItems.filter(
      (orderItem) => orderItem.product.productCategory.productCategoryType === ProductCategoryType.Consumable
    );
    return consumables.length;
  }

  checkPendingAcessories(sling: Sling): boolean {
    const acessories = sling.slingAccessories.filter((x) => !x.attended);
    return acessories.length > 0;
  }

  checkPendingConsumables(orderItems: OrderItem[]): boolean {
    const consumables = orderItems.filter((orderItem) =>
      orderItem.product.productCategory.productCategoryType === ProductCategoryType.Consumable && !orderItem.itemStatus);
    return consumables.length > 0;
  }

  checkPendingFuel(orderItems: OrderItem[]): boolean {
    const fuels = orderItems.filter((orderItem) =>
      orderItem.product.productCategory.productCategoryType === ProductCategoryType.Fuel && !orderItem.itemStatus);
    return fuels.length > 0;
  }

  hasBoatSailor(sling: Sling): boolean {
    return !!sling.boatSailor;
  }

  getImageSailorResponsable() {
    return '/assets/images/sailorOnSlingBlack.png';
  }

  hasSailorConfig(): boolean{
    return this.slingConfig.hasSailor;
  }
}
